
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface VideoContent {
  id: number;
  title: string;
  description: string;
  duration: string;
  thumbnail_url?: string;
  video_url: string;
  upload_date: string;
  category: string;
  artist?: string;
  play_count: number;
}

export default function VideoDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoId = searchParams.get('id');
  const part = searchParams.get('part');
  
  const [video, setVideo] = useState<VideoContent | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<VideoContent[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [language, setLanguage] = useState<'ko' | 'en'>('ko');
  const [showControls, setShowControls] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 파트별 정보
  const partInfo = {
    '1': {
      title: 'Chapter 1: 첫 번째 이야기',
      subtitle: '시작 X 2005',
      description: '1945년 개관 이후 국립중앙박물관이 걸어온 길을 되돌아봅니다.',
      bgColor: 'bg-purple-600'
    },
    '2': {
      title: 'Chapter 2: 두 번째 이야기', 
      subtitle: '발견 X 깊이',
      description: '한국의 대표적인 문화유산들이 어떻게 발견되고 수집되었는지 살펴봅니다.',
      bgColor: 'bg-amber-500'
    },
    '3': {
      title: 'Chapter 3: 세 번째 이야기',
      subtitle: '감동 X 전시',
      description: '첨단 과학기술을 활용한 문화유산 보존 기법과 연구 성과를 소개합니다.',
      bgColor: 'bg-green-600'
    },
    '4': {
      title: 'Chapter 4: 네 번째 이야기',
      subtitle: '확장 X 미래', 
      description: '디지털 시대에 맞춘 박물관의 새로운 역할과 비전을 제시합니다.',
      bgColor: 'bg-blue-600'
    }
  };

  // 언어별 텍스트
  const texts = {
    ko: {
      backToHome: '홈으로 돌아가기',
      relatedVideos: '관련 영상',
      playButton: '재생',
      pauseButton: '일시정지',
      subtitles: '자막',
      language: '언어',
      korean: '한국어',
      english: 'English',
      volume: '음량',
      fullscreen: '전체화면',
      loading: '비디오를 불러오는 중...',
      noVideo: '비디오를 찾을 수 없습니다',
      error: '오류가 발생했습니다',
      views: '조회수',
      uploadDate: '업로드 날짜'
    },
    en: {
      backToHome: 'Back to Home',
      relatedVideos: 'Related Videos',
      playButton: 'Play',
      pauseButton: 'Pause',
      subtitles: 'Subtitles',
      language: 'Language',
      korean: '한국어',
      english: 'English',
      volume: 'Volume',
      fullscreen: 'Fullscreen',
      loading: 'Loading video...',
      noVideo: 'Video not found',
      error: 'An error occurred',
      views: 'Views',
      uploadDate: 'Upload Date'
    }
  };

  const t = texts[language];

  // 비디오 데이터 가져오기
  const fetchVideo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-video-contents?active_only=true&language=${language}`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const result = await response.json();
      const videos: VideoContent[] = result.data || [];
      
      let targetVideo: VideoContent | null = null;

      // ID로 비디오 찾기
      if (videoId) {
        targetVideo = videos.find(v => v.id === parseInt(videoId)) || null;
      }
      // 파트로 비디오 찾기
      else if (part) {
        targetVideo = videos.find(v => 
          v.category === `part${part}` || 
          v.title.includes(`${part}부`) ||
          v.title.includes(`Part ${part}`)
        ) || null;
      }

      if (targetVideo) {
        setVideo(targetVideo);
        
        // 조회수 업데이트
        updatePlayCount(targetVideo.id);
        
        // 관련 비디오 설정 (같은 카테고리 또는 다른 파트들)
        const related = videos.filter(v => 
          v.id !== targetVideo!.id && 
          (v.category === targetVideo!.category || v.category.startsWith('part'))
        ).slice(0, 4);
        setRelatedVideos(related);
      } else {
        setError('비디오를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      setError('비디오를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 조회수 업데이트
  const updatePlayCount = async (videoId: number) => {
    try {
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-play-count`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ video_id: videoId }),
        }
      );
    } catch (error) {
      console.error('Error updating play count:', error);
    }
  };

  useEffect(() => {
    fetchVideo();
  }, [videoId, part, language]);

  // 컨트롤 자동 숨김
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showControls && isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 3000);
    }
    return () => clearTimeout(timeout);
  }, [showControls, isPlaying]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
    setShowControls(true);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    setDuration(videoRef.current.duration || 0);
  };

  const handleSeek = (time: number) => {
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const skipForward = () => {
    const newTime = Math.min(currentTime + 10, duration);
    handleSeek(newTime);
  };

  const skipBackward = () => {
    const newTime = Math.max(currentTime - 10, 0);
    handleSeek(newTime);
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    
    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen().catch(err => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().catch(err => {
        console.error('Exit fullscreen error:', err);
      });
    }
  };

  const handleRelatedVideoClick = (relatedVideo: VideoContent) => {
    navigate(`/video-detail?id=${relatedVideo.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-400 mb-4"></i>
          <p className="text-gray-600 text-lg mb-4">{error || t.noVideo}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            {t.backToHome}
          </button>
        </div>
      </div>
    );
  }

  const currentPartInfo = part ? partInfo[part as keyof typeof partInfo] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <img
                src="https://static.readdy.ai/image/0a66c5de716ec38ae571e4aeedf62bcb/e2a1a91622c60ac50ace1ab033496245.png"
                alt="National Museum of Korea Logo"
                className="h-10 w-auto object-contain cursor-pointer"
                onClick={() => navigate('/')}
              />
              <h1 className="text-xl font-semibold text-gray-900">National Museum of Korea</h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* 언어 선택 */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('ko')}
                  className={`px-3 py-1 text-sm rounded transition-colors cursor-pointer whitespace-nowrap ${
                    language === 'ko'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  한국어
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 text-sm rounded transition-colors cursor-pointer whitespace-nowrap ${
                    language === 'en'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  EN
                </button>
              </div>
              
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer flex items-center whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                {t.backToHome}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 메인 비디오 플레이어 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 파트 정보 (있는 경우) */}
            {currentPartInfo && (
              <div className={`${currentPartInfo.bgColor} text-white rounded-lg p-6`}>
                <h1 className="text-2xl font-bold mb-2">{currentPartInfo.title}</h1>
                <p className="text-lg opacity-90">{currentPartInfo.subtitle}</p>
              </div>
            )}

            {/* 비디오 플레이어 */}
            <div 
              className="relative bg-black rounded-lg overflow-hidden aspect-video cursor-pointer"
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
              onMouseMove={() => setShowControls(true)}
            >
              <video
                ref={videoRef}
                src={video.video_url}
                poster={video.thumbnail_url}
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleTimeUpdate}
                onEnded={() => setIsPlaying(false)}
              />
              
              {/* 재생 버튼 오버레이 */}
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={handlePlayPause}
                    className="w-24 h-24 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all cursor-pointer shadow-lg"
                  >
                    <i className="ri-play-fill text-4xl text-gray-800 ml-1"></i>
                  </button>
                </div>
              )}

              {/* 비디오 컨트롤 */}
              <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-6 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
                {/* 진행률 바 */}
                <div className="mb-4">
                  <div 
                    className="w-full bg-gray-600 rounded-full h-2 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      handleSeek(percent * duration);
                    }}
                  >
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-300 mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-4">
                    {/* 재생/일시정지 */}
                    <button
                      onClick={handlePlayPause}
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all cursor-pointer"
                    >
                      <i className={`ri-${isPlaying ? 'pause' : 'play'}-fill text-xl`}></i>
                    </button>
                    
                    {/* 되감기 */}
                    <button
                      onClick={skipBackward}
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all cursor-pointer"
                    >
                      <i className="ri-rewind-fill text-xl"></i>
                    </button>
                    
                    {/* 빨리감기 */}
                    <button
                      onClick={skipForward}
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all cursor-pointer"
                    >
                      <i className="ri-speed-fill text-xl"></i>
                    </button>

                    {/* 볼륨 */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{t.volume}</span>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        className="w-24 accent-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* 자막 토글 */}
                    <button
                      onClick={() => setShowSubtitles(!showSubtitles)}
                      className={`px-3 py-1 rounded-full text-sm cursor-pointer ${
                        showSubtitles ? 'bg-blue-500 text-white' : 'bg-white bg-opacity-20 text-gray-300'
                      }`}
                    >
                      {t.subtitles}
                    </button>

                    {/* 전체화면 */}
                    <button
                      onClick={toggleFullscreen}
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all cursor-pointer"
                    >
                      <i className="ri-fullscreen-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 비디오 정보 */}
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{video.title}</h2>
              
              <div className="flex items-center space-x-6 text-sm text-gray-500 mb-4">
                <span className="flex items-center">
                  <i className="ri-eye-line mr-1"></i>
                  {video.play_count.toLocaleString()} {t.views}
                </span>
                <span className="flex items-center">
                  <i className="ri-time-line mr-1"></i>
                  {video.duration}
                </span>
                <span className="flex items-center">
                  <i className="ri-calendar-line mr-1"></i>
                  {new Date(video.upload_date).toLocaleDateString()}
                </span>
                {video.artist && (
                  <span className="flex items-center">
                    <i className="ri-user-line mr-1"></i>
                    {video.artist}
                  </span>
                )}
              </div>

              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">{video.description}</p>
              </div>
            </div>
          </div>

          {/* 사이드바 - 관련 비디오 */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-play-list-line mr-2"></i>
                {t.relatedVideos}
              </h3>
              
              <div className="space-y-4">
                {relatedVideos.map((relatedVideo) => (
                  <div
                    key={relatedVideo.id}
                    onClick={() => handleRelatedVideoClick(relatedVideo)}
                    className="flex space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-24 h-16 bg-gray-200 rounded-lg overflow-hidden">
                        {relatedVideo.thumbnail_url ? (
                          <img
                            src={relatedVideo.thumbnail_url}
                            alt={relatedVideo.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-play-circle-line text-2xl text-gray-400"></i>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                        {relatedVideo.title}
                      </h4>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span>{relatedVideo.duration}</span>
                        <span>•</span>
                        <span>{relatedVideo.play_count.toLocaleString()} 조회</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {relatedVideos.length === 0 && (
                <div className="text-center py-8">
                  <i className="ri-video-line text-4xl text-gray-300 mb-2"></i>
                  <p className="text-gray-500">관련 비디오가 없습니다</p>
                </div>
              )}
            </div>

            {/* 다른 챕터로 이동 */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">다른 챕터 보기</h3>
              <div className="space-y-2">
                {Object.entries(partInfo).map(([partNum, info]) => (
                  <button
                    key={partNum}
                    onClick={() => navigate(`/video-detail?part=${partNum}`)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                      part === partNum 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{info.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{info.subtitle}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
